import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { HiBell } from 'react-icons/hi';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import { useIntegrationStatus } from '../../../hooks/useIntegrationStatus';
import { useOrganizationService, useStrategyMapService } from '../../../services';
import useSlackService from '../../../services/slack';
import { useJwt } from '../../../helpers/common';
import { buildEntityDetailSummary, getEntitySelectionType, getEntityTypeLabel, } from '../../../utils/entityTypeUtils';
import {
  AvailableNotification,
  FrequencyType,
  NotificationTrigger,
  NotificationTriggerResponse,
  SlackChannel,
  SlackUser,
  TaskTriggerType
} from "../types";
import TriggerWizard, { TriggerConfig } from "./components/TriggerWizard";
import {
  setSlackWizardHasFetchedSprints,
  setSlackWizardLoadingSprints,
  setSlackWizardSprints,
  setSlackWizardStep
} from "../../../redux/slices/settings";
import TriggersSkeleton from './components/TriggersSkeleton';
import TriggersEmpty from './components/TriggersEmpty';
import TriggersList from './components/TriggersList';
import NotificationsHeader from './components/NotificationsHeader';

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isIntegrationActive } = useIntegrationStatus();
  const isSlackConnected = isIntegrationActive('slack');
  const isAzureConnected = isIntegrationActive('azure');
  const isJiraConnected = isIntegrationActive('jira');

  const { fetchLeveldSmap } = useStrategyMapService();
  const { fetchAllProjects, fetchSprints } = useOrganizationService();
  const {
    getSlackTargets,
    fetchNotificationTriggers,
    createNotificationTrigger,
    updateNotificationTrigger,
    fetchAvailableNotifications,
    updateSlackNotificationStatus,
    deleteNotificationTriggerOptimistic,
    toggleNotificationTriggerOptimistic
  } = useSlackService();

  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;

  const {
    data: projectsData
  } = useSelector((state: any) => state.organization);

  const {
    smaps = []
  } = useSelector((state: any) => state.levelMapList);

  const {
    slackTriggers: triggersFromRedux = [],
    slackTriggersLoading: triggersLoading = false,
    slackChannels: slackChannelsFromRedux = [],
    slackUsers: slackUsersFromRedux = [],
    slackAvailableNotifications: availableNotifications = [],
    slackTargetsLoading: targetsLoading = false,
    slackAvailableNotificationsLoading: availableNotificationsLoading = false,
    slackWizardSprints: sprints = [],
    slackWizardHasFetchedSprints: hasFetchedSprints = false,
  } = useSelector((state: any) => state.settings);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(isSlackConnected);
  const [notificationStatusLoading, setNotificationStatusLoading] = useState(false);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const [wizardMode, setWizardMode] = useState<'create' | 'edit'>('create');
  const [wizardInitialConfig, setWizardInitialConfig] = useState<TriggerConfig | undefined>();
  const [triggerBeingEdited, setTriggerBeingEdited] = useState<NotificationTrigger | null>(null);

  useEffect(() => {
    if (!isSlackConnected) {
      setNotificationsEnabled(false);
      setNotificationStatusLoading(false);
    }
  }, [isSlackConnected]);

  // Initial loads when org and slack are ready
  useEffect(() => {
    if (!org_id) return;
    setHasInitialLoadCompleted(false);
    if (isSlackConnected) {
      fetchAllProjects(org_id);
      fetchLeveldSmap(1, 50, '', 'smap', null, null);
      Promise.all([
        getSlackTargets(org_id),
        fetchNotificationTriggers(org_id),
        fetchAvailableNotifications(org_id)
      ]).then(([, , data]) => {
        if (typeof data?.enabled === 'boolean') {
          setNotificationsEnabled(data.enabled);
        }
        setHasInitialLoadCompleted(true);
      }).catch(() => {
        setHasInitialLoadCompleted(true);
      });
    } else {
      fetchAvailableNotifications(org_id)
        .then((data) => {
          if (data && typeof data.enabled === 'boolean') {
            setNotificationsEnabled(data.enabled);
          }
        })
        .finally(() => setHasInitialLoadCompleted(true));
    }
  }, [org_id, isSlackConnected]);

  const refetchTriggers = useCallback(async () => {
    if (!org_id || !isSlackConnected) return;
    await fetchNotificationTriggers(org_id);
  }, [org_id, isSlackConnected]);

  const loadSprints = useCallback(async () => {
    if (hasFetchedSprints) return;

    if (!isAzureConnected && !isJiraConnected) {
      try {
        dispatch(setSlackWizardSprints([]));
        dispatch(setSlackWizardHasFetchedSprints(true));
      } catch (e) { }
      return;
    }

    dispatch(setSlackWizardLoadingSprints(true));

    try {
      const statusFilter = 'current';
      const azurePromise = isAzureConnected ? fetchSprints('azure', statusFilter) : Promise.resolve([]);
      const jiraPromise = isJiraConnected ? fetchSprints('jira', statusFilter) : Promise.resolve([]);
      const [azureSprints, jiraSprints] = await Promise.all([azurePromise, jiraPromise]);
      const azureWithType = (azureSprints || []).map((s: any) => ({ ...s, type: 'azure' }));
      const jiraWithType = (jiraSprints || []).map((s: any) => ({ ...s, type: 'jira' }));
      const combinedSprints = [...azureWithType, ...jiraWithType];
      dispatch(setSlackWizardSprints(combinedSprints));
      dispatch(setSlackWizardHasFetchedSprints(true));
    } catch (error) {
      console.error('Error loading sprints:', error);
      dispatch(setSlackWizardSprints([]));
      dispatch(setSlackWizardHasFetchedSprints(true));
    } finally {
      dispatch(setSlackWizardLoadingSprints(false));
    }
  }, [hasFetchedSprints, isAzureConnected, isJiraConnected]);

  useEffect(() => {
    if (showConfigModal && !hasFetchedSprints) {
      loadSprints();
    }
  }, [showConfigModal, hasFetchedSprints]);

  const handleNavigateToSlack = () => {
    navigate({ pathname: '/settings', hash: '#Integrations', search: '?open=slack' });
  };

  const handleToggleNotifications = useCallback(async () => {
    if (notificationStatusLoading) {
      return;
    }

    if (!isSlackConnected) {
      handleNavigateToSlack();
      return;
    }

    const nextValue = !notificationsEnabled;
    setNotificationStatusLoading(true);
    setNotificationsEnabled(nextValue);
    await updateSlackNotificationStatus(nextValue);
    setNotificationStatusLoading(false);
  }, [isSlackConnected, notificationsEnabled, notificationStatusLoading, updateSlackNotificationStatus]);

  const strategyMapOptions = useMemo(() => {
    return (smaps || [])
      .map((smap: any) => ({
        value: String(smap?.id ?? ''),
        label: smap?.name ?? 'Strategy Map',
      }))
      .filter((option: { value: string }) => option.value)
  }, [smaps]);

  const projectOptions = useMemo(() => {
    return (projectsData || [])
      .map((project: any) => ({
        value: String(project?.project_id ?? project?.id ?? ''),
        label: project?.project_name ?? project?.name ?? 'Work Item',
      }))
      .filter((option: { value: string }) => option.value)
  }, [projectsData]);

  const sprintTeamOptions = useMemo(() => {
    return (sprints || [])
      .filter((sprint: any) => sprint?.id != null && sprint?.team_name != null)
      .map((sprint: any) => {
        const teamLabel = sprint.team_name || 'Team';
        const projectLabel = sprint.project_name || sprint.project || '';
        const label = projectLabel ? `${teamLabel} (${projectLabel})` : teamLabel;
        return { value: String(sprint.id), label };
      });
  }, [sprints]);

  const buildDetails = useCallback((entityType: string, entityNames: string[], config: Partial<TriggerConfig>, selectionScope?: 'active' | 'selective') => {
    const selectionType = getEntitySelectionType(entityType);

    if (selectionScope === 'active') {
      if (selectionType === 'strategy_map') {
        return 'Active strategy maps';
      } else if (selectionType === 'work_item') {
        return 'Active work items';
      }
      return 'Active';
    }

    if (selectionType === 'task') {
      const eventCount = config.taskTriggerTypes?.length || 0;
      return eventCount === 1 ? '1 event' : `${eventCount} events`;
    }

    if (selectionType === 'sprint') {
      const teamCount = entityNames.length;
      return teamCount === 1 ? '1 team' : `${teamCount} teams`;
    }

    return buildEntityDetailSummary(
      entityType,
      entityNames.length,
      { triggerCount: config.taskTriggerTypes?.length }
    );
  }, []);

  // Transform Redux triggers to UI format
  const transformedTriggers = useMemo(() => {
    console.log("triggersFromRedux", triggersFromRedux);
    return triggersFromRedux.map((trigger: NotificationTriggerResponse) => {
      const entityType = trigger.entity_type;
      const sendToLabels = (trigger.send_to || []).map((target: any) => {
        if (target.target_type === 'channel') {
          const channel = slackChannelsFromRedux.find((c: SlackChannel) => c.id === target.slack_id);
          return channel ? `#${channel.name}` : target.label || target.slack_id;
        } else {
          const user = slackUsersFromRedux.find((u: SlackUser) => u.slack_user_id === target.slack_id);
          return user ? `@${user.name}` : target.label || target.slack_id;
        }
      });

      // Extract entity IDs from configuration
      const entityIds: string[] = [];
      const entityNames: string[] = [];
      let selectionScope: 'active' | 'selective' | undefined = undefined;
      let cronConfig: { day: number; hour: number; minute: number; timezone: string } | undefined = undefined;
      let entitiesDataForSprint: any[] | undefined = undefined;
      let sprintIntegrationForTrigger: 'jira' | 'azure' | undefined = undefined;

      if (trigger.configuration && Array.isArray(trigger.configuration)) {
        trigger.configuration.forEach((config: any) => {
          if (config?.type === 'active') {
            selectionScope = 'active';
          } else if (config?.type === 'selective') {
            selectionScope = 'selective';
          }
          if (config.entity_id && Array.isArray(config.entity_id)) {
            entityIds.push(...config.entity_id);
          }
          // Handle entity_list for work_item entity type
          if (entityType === 'work_item' && config.entity_list && Array.isArray(config.entity_list)) {
            config.entity_list.forEach((item: any) => {
              if (item.entity_id) {
                entityIds.push(item.entity_id);
              }
            });
          }
          // Handle entity_list + sprint_integration for sprint entity type. Resolve to sprint IDs for dropdown (options use sprint.id).
          if (entityType === 'sprint_report' && config.sprint_integration && config.entity_list && Array.isArray(config.entity_list)) {
            sprintIntegrationForTrigger = config.sprint_integration;
            entitiesDataForSprint = config.entity_list.map((item: any) => {
              if (config.sprint_integration === 'azure') {
                const key = `${item.project_id}:${item.team_id}`;
                return { entity_id: key, project_id: item.project_id, team_id: item.team_id };
              }
              const key = `${item.board_id}:${item.project_id}`;
              return { entity_id: key, board_id: item.board_id, project_id: item.project_id };
            });
            config.entity_list.forEach((item: any) => {
              const match = (sprints || []).find((s: any) => {
                if (config.sprint_integration === 'azure') {
                  return item.project_id != null && item.team_id != null && String(s?.project_id) === String(item.project_id) && String(s?.team_id) === String(item.team_id);
                }
                return item.board_id != null && item.project_id != null && String(s?.team_id) === String(item.board_id) && String(s?.project_id) === String(item.project_id);
              });
              if (match?.id != null) entityIds.push(String(match.id));
              else if (config.sprint_integration === 'azure' && item.project_id != null && item.team_id != null) {
                entityIds.push(`${item.project_id}:${item.team_id}`);
              } else if (config.sprint_integration === 'jira' && item.board_id != null && item.project_id != null) {
                entityIds.push(`${item.board_id}:${item.project_id}`);
              }
            });
          }
          // Extract cron config if present
          if (config.cron) {
            cronConfig = {
              day: config.cron.day ?? 1,
              hour: config.cron.hour ?? 9,
              minute: config.cron.minute ?? 0,
              timezone: config.cron.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC'
            };
          }
        });
      }

      // Build entity names based on type using dynamic selection type
      const selectionType = getEntitySelectionType(entityType);
      let taskTriggerTypes: TaskTriggerType[] = [];
      if (selectionType === 'task' && trigger.configuration && Array.isArray(trigger.configuration)) {
        trigger.configuration.forEach((config: any) => {
          if (config.triggers && Array.isArray(config.triggers)) {
            taskTriggerTypes = config.triggers as TaskTriggerType[];
          }
        });
      }

      if (selectionType === 'strategy_map') {
        entityIds.forEach((id) => {
          const sm = strategyMapOptions.find((s: any) => s.value === id);
          entityNames.push(sm?.label || 'Strategy Map');
        });
      } else if (selectionType === 'work_item' || entityType === 'work_item') {
        entityIds.forEach((id) => {
          const proj = projectOptions.find((p: any) => p.value === id);
          entityNames.push(proj?.label || 'Work Item');
        });
      } else if (selectionType === 'sprint') {
        entityIds.forEach((id) => {
          const team = sprintTeamOptions.find((t: any) => t.value === id);
          entityNames.push(team?.label || 'Team');
        });
      }

      const matchingNotification = availableNotifications.find((notif: AvailableNotification) =>
        notif.id === trigger.notification_type_id || notif.key === trigger.notification_key
      );
      const supportedFrequencies = (matchingNotification?.metadata?.supports_frequencies || []) as FrequencyType[];
      const frequency = (trigger.frequency || matchingNotification?.default_frequency || 'daily') as FrequencyType;

      return {
        id: String(trigger.id),
        createdAt: trigger.created_at || '',
        entity_type: entityType,
        entityTypeLabel: getEntityTypeLabel(entityType),
        entity_ids: entityIds,
        entity_names: entityNames,
        frequency,
        send_to: sendToLabels,
        details: buildDetails(entityType, entityNames, { taskTriggerTypes }, selectionScope),
        enabled: trigger.status === 'enabled',
        taskTriggerTypes,
        notificationKey: trigger.notification_key || matchingNotification?.key || '',
        supportedFrequencies,
        selectionScope,
        cron: cronConfig,
        entitiesData: entitiesDataForSprint,
        sprintIntegration: sprintIntegrationForTrigger,
      };
    });
  }, [triggersFromRedux, slackChannelsFromRedux, slackUsersFromRedux, strategyMapOptions, projectOptions, sprintTeamOptions, availableNotifications, buildDetails, sprints]);

  const handleDeleteTrigger = useCallback(async (triggerId: string) => {
    if (!org_id) return;
    await deleteNotificationTriggerOptimistic(org_id, Number(triggerId));
  }, [org_id, deleteNotificationTriggerOptimistic]);

  const handleToggleTrigger = useCallback(async (triggerId: string) => {
    if (!org_id) return;
    await toggleNotificationTriggerOptimistic(org_id, Number(triggerId));
  }, [org_id, toggleNotificationTriggerOptimistic]);

  const handleWizardComplete = async (config: TriggerConfig) => {
    if (!org_id) return;

    const notificationDefinition = availableNotifications.find((notif: AvailableNotification) => notif.key === config.notificationKey);

    const supportedFrequencies = config.supportedFrequencies?.length
      ? config.supportedFrequencies
      : ((notificationDefinition?.metadata?.supports_frequencies || []) as FrequencyType[]);

    const normalizedFrequency = supportedFrequencies.includes(config.frequency)
      ? config.frequency
      : ((supportedFrequencies[0] || (notificationDefinition?.default_frequency as FrequencyType) || 'daily') as FrequencyType);

    // Build send_to targets from selected channels/users
    const sendToTargets = config.sendToChannels.map((channelId) => {
      const channel = slackChannelsFromRedux.find((c: SlackChannel) => c.id === channelId);
      const user = slackUsersFromRedux.find((u: SlackUser) => u.slack_user_id === channelId);

      if (channel) {
        return {
          target_type: 'channel' as const,
          slack_id: channel.id,
          // label: `#${channel.name}`,
          metadata: {}
        };
      } else if (user) {
        return {
          target_type: 'user' as const,
          slack_id: user.slack_user_id,
          // label: `@${user.name}`,
          metadata: {}
        };
      }
      return null;
    }).filter(Boolean) as any[];

    // Build configuration based on entity type
    const configuration: any[] = [];
    const selectionType = getEntitySelectionType(config.entityType);

    if (selectionType === 'task') {
      // For task changes, use triggers array
      configuration.push({
        triggers: config.taskTriggerTypes || []
      });
    } else if (selectionType === 'work_item' && config.entitiesData && config.entitiesData.length > 0) {
      // For work_item with entities data, build single config object with entity_list
      const scope = (config.selectionScope || ((config.entityIds.length > 0) ? 'selective' : 'active'));
      
      const configItem: any = {
        type: scope,
      };

      if (scope === 'selective') {
        // Build entity_list with entity_id and type for each entity
        configItem.entity_list = config.entitiesData.map((entityData) => ({
          entity_id: entityData.entity_id,
          type: entityData.type
        }));
      }

      // Add cron config for scheduled notifications
      if (config.cron && config.frequency !== 'immediate') {
        configItem.cron = {
          day: config.cron.day,
          hour: config.cron.hour,
          minute: config.cron.minute,
          timezone: config.cron.timezone
        };
      }

      configuration.push(configItem);
    } else if (selectionType === 'sprint' && config.sprintIntegration && config.entitiesData && config.entitiesData.length > 0) {
      // For sprint with entity_list only (Azure: project_id, team_id; JIRA: board_id, project_id). No entity_id.
      const scope = 'selective';
      const configItem: any = {
        type: scope,
        sprint_integration: config.sprintIntegration,
        entity_list: config.sprintIntegration === 'azure'
          ? config.entitiesData.map((e: any) => ({ project_id: e.project_id, team_id: e.team_id }))
          : config.entitiesData.map((e: any) => ({ board_id: e.board_id, project_id: e.project_id })),
      };
      if (config.cron && config.frequency !== 'immediate') {
        configItem.cron = {
          day: config.cron.day,
          hour: config.cron.hour,
          minute: config.cron.minute,
          timezone: config.cron.timezone
        };
      }
      configuration.push(configItem);
    } else {
      // For entity-scoped types (strategy_map, sprint legacy, etc.), honor selection scope explicitly
      const scope = (config.selectionScope || ((config.entityIds.length > 0) ? 'selective' : 'active'));
      const configItem: any = {
        type: scope,
      };

      if (scope === 'selective') {
        configItem.entity_id = config.entityIds;
      }

      // Add cron config for scheduled notifications
      if (config.cron && config.frequency !== 'immediate') {
        configItem.cron = {
          day: config.cron.day,
          hour: config.cron.hour,
          minute: config.cron.minute,
          timezone: config.cron.timezone
        };
      }

      configuration.push(configItem);
    }

    // Use the backend entity type directly from notification definition
    // Transform 'work_item' to 'work_item' for the API payload
    let apiEntityType = notificationDefinition?.entity_type || config.entityType;
    if (apiEntityType === 'work_item') {
      apiEntityType = 'work_item';
    }

    const defaultFrequencyFromNotification = (notificationDefinition?.default_frequency || 'daily') as FrequencyType;
    const requestFrequency = (normalizedFrequency || defaultFrequencyFromNotification) as 'immediate' | 'daily' | 'weekly' | 'biweekly' | 'monthly';

    const requestData = {
      notification_id: notificationDefinition?.id || 0,
      entity_type: apiEntityType,
      frequency: requestFrequency,
      send_to: sendToTargets,
      configuration,
      status: 'enabled' as const
    };

    if (wizardMode === 'edit' && triggerBeingEdited) {
      const updated = await updateNotificationTrigger(org_id, Number(triggerBeingEdited.id), requestData);
      if (updated) {
        await refetchTriggers();
      }
    } else {
      const created = await createNotificationTrigger(org_id, requestData);
      if (created) {
        await refetchTriggers();
      }
    }

    setShowConfigModal(false);
    setTriggerBeingEdited(null);
    setWizardMode('create');
    setWizardInitialConfig(undefined);
  };

  const handleAddClick = () => {
    setWizardMode('create');
    setTriggerBeingEdited(null);
    setWizardInitialConfig(undefined);
    setShowConfigModal(true);
  };

  const handleCancelForm = () => {
    setShowConfigModal(false);
    setTriggerBeingEdited(null);
    setWizardMode('create');
    setWizardInitialConfig(undefined);
  };

  const buildInitialConfig = useCallback((trigger: NotificationTrigger): TriggerConfig => {
    // Map send_to labels back to channel/user IDs
    const sendToIds = trigger.send_to.map((label) => {
      // Check if it's a channel (starts with #)
      if (label.startsWith('#')) {
        const channelName = label.substring(1);
        const channel = slackChannelsFromRedux.find((c: SlackChannel) => c.name === channelName);
        return channel?.id || label;
      }
      // Check if it's a user (starts with @)
      if (label.startsWith('@')) {
        const userName = label.substring(1);
        const user = slackUsersFromRedux.find((u: SlackUser) => u.name === userName);
        return user?.slack_user_id || label;
      }
      return label;
    });

    const matchingNotification = availableNotifications.find((notif: AvailableNotification) => notif.key === trigger.notificationKey);
    const supportedFrequencies = (matchingNotification?.metadata?.supports_frequencies || trigger.supportedFrequencies || []) as FrequencyType[];

    return {
      entityType: trigger.entity_type,
      entityIds: trigger.entity_ids,
      frequency: trigger.frequency,
      sendToChannels: sendToIds,
      taskTriggerTypes: trigger.taskTriggerTypes,
      notificationKey: trigger.notificationKey,
      supportedFrequencies,
      selectionScope: (trigger as any).selectionScope,
      cron: (trigger as any).cron,
      entitiesData: (trigger as any).entitiesData,
      sprintIntegration: (trigger as any).sprintIntegration,
    };
  },
    [slackChannelsFromRedux, slackUsersFromRedux, availableNotifications]
  );

  const handleEditTrigger = useCallback((trigger: NotificationTrigger) => {
    // Ensure wizard seeds fresh for this trigger
    dispatch(setSlackWizardStep('entity_type'));
    setTriggerBeingEdited(trigger);
    setWizardMode('edit');
    setWizardInitialConfig(buildInitialConfig(trigger));
    setShowConfigModal(true);
  },
    [buildInitialConfig]
  );

  const sortedTriggers = useMemo(() => {
    const data = [...transformedTriggers] as any[];
    data.sort((a, b) => {
      const ta = new Date(a?.createdAt || 0).getTime();
      const tb = new Date(b?.createdAt || 0).getTime();
      return tb - ta; // most recent first
    });
    return data as any;
  }, [transformedTriggers]);

  const triggersCount: number = Array.isArray(transformedTriggers) ? transformedTriggers.length : 0;

  const getFrequencyBadgeColor = (frequency: FrequencyType) => {
    const colors: Record<FrequencyType, { bg: string; text: string }> = {
      immediate: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
      daily: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
      weekly: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
      biweekly: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300' },
      monthly: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
    };
    return colors[frequency] || colors.daily;
  };

  return (
    <div className="min-h-screen bg-light-surfacePrimary dark:bg-tertiary-dark w-full">
      <NotificationsHeader />

      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Main Notification Triggers Card */}
          <div className="bg-white dark:bg-secondary-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="px-8 py-5 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-light/10 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark">
                    <HiBell className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white font-['Inter']">
                      Slack Notification Triggers
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-['Inter'] mt-1">
                      Configure automated updates for strategy maps, work items, sprints and tasks.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center">
                    <ToggleSwitch
                      id="notifications-enabled"
                      name="notifications-enabled"
                      label=""
                      checked={notificationsEnabled}
                      onChange={async (e) => {
                        await handleToggleNotifications();
                      }}
                      className="flex items-center"
                      disabled={notificationStatusLoading}
                    />
                  </div>
                  <div className="h-4 min-h-[16px]">
                    {!notificationsEnabled && isSlackConnected && (
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 text-right">
                        Enable notifications to add or edit triggers
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className={`border-t border-gray-200 dark:border-gray-700 pt-5 ${isSlackConnected && !notificationsEnabled ? 'opacity-50' : ''}`}>
                {!hasInitialLoadCompleted || targetsLoading || availableNotificationsLoading || triggersLoading ? (
                  <TriggersSkeleton />
                ) : !isSlackConnected ? (
                  <TriggersEmpty
                    triggersCount={triggersCount}
                    notificationsEnabled={notificationsEnabled}
                    isSlackConnected={isSlackConnected}
                    onAddClick={() => setShowConfigModal(true)}
                    onNavigateToSlack={handleNavigateToSlack}
                    variant="connect"
                  />
                ) : triggersCount === 0 ? (
                  <TriggersEmpty
                    triggersCount={triggersCount}
                    notificationsEnabled={notificationsEnabled}
                    isSlackConnected={isSlackConnected}
                    onAddClick={handleAddClick}
                    variant="empty"
                  />
                ) : (
                  <TriggersList
                    triggers={sortedTriggers}
                    onEdit={handleEditTrigger}
                    onToggle={handleToggleTrigger}
                    onDelete={handleDeleteTrigger}
                    getFrequencyBadgeColor={getFrequencyBadgeColor}
                    canAdd={notificationsEnabled}
                    onAddClick={handleAddClick}
                  />
                )}
              </div>
            </div>

            {/* Footer Actions */}
            {/* {isSlackConnected && (
              <FooterActions
                isSaving={isSaving}
                onSave={handleSaveConfiguration}
              />
            )} */}
          </div>
        </div>

        <TriggerWizard
          isOpen={showConfigModal}
          onComplete={handleWizardComplete}
          onCancel={handleCancelForm}
          mode={wizardMode}
          initialConfig={wizardInitialConfig}
        />
      </div>
    </div>
  );
};

export default Notifications;
